import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { type RepositoryInfo } from '@terra/ui-design-system';
import { gitClient } from '@terra/shared-rest-clients-git';

export function useRepositoryInfoMap({
  applicationId,
}: {
  applicationId: string | undefined;
}) {
  const { data: gitIntegrationResponse } = useQuery({
    queryKey: ['git-integration', applicationId],
    queryFn: async () => {
      if (!applicationId) return null;
      return gitClient.getApplicationGitIntegrations({
        params: { applicationId },
      });
    },
    enabled: !!applicationId,
    refetchOnWindowFocus: false,
    staleTime: 30000,
  });

  const repositoryInfoMap = useMemo(() => {
    if (
      gitIntegrationResponse?.status !== 200 ||
      !gitIntegrationResponse.body?.gitIntegrations?.length
    ) {
      return {};
    }

    const repoMap: Record<string, RepositoryInfo> = {};

    gitIntegrationResponse.body.gitIntegrations.forEach((gitIntegration) => {
      gitIntegration.repositories.forEach((repo) => {
        if (!repo.name || !repo.owner) return;

        repoMap[repo.name] = {
          platform: gitIntegration.platform,
          owner: repo.owner,
          repositoryName: repo.name,
          branch: repo.targetBranch || 'main',
          baseUrl: gitIntegration.baseUrl,
        };
      });
    });

    return repoMap;
  }, [gitIntegrationResponse]);

  return repositoryInfoMap;
}
